// ── Статистика: прогресс по языку, слушание и чтение, календарь ────────────────

import { useEffect, useMemo, useState } from "react";
import { Heatmap } from "../components/Heatmap";
import { t } from "../i18n";
import Icon from "../Icon";
import { markSetting } from "../settings";
import { SANS } from "../themes";
import { adjustTime, adjustWordsRead, calcStats, getActiveLang, getActivityData, getAllStatsLangs, getStudyDays, getTimeData, getWordsReadData, hasUnassignedStats, nDays, ruPlural } from "../utils";
import { useWordStore } from "../wordLearning";
import { Button, Chip, Field, Sheet } from "../ui";
import Confirm from "../ui/Confirm";
import { readableOn } from "../ui/color";
import { usePalette } from "../ui/theme";
import { panelSurface } from '../ui/surface';

// ── StatsScreen — прогресс по языкам, разделено на слушание и чтение ────────
// CEFR-уровни:
//  Чтение — от Nation/extensive reading: ~3M слов ≈ 8K активного словаря (C2 fluency).
//  Слушание — от FSI: классов 600–2200ч до B2-C1; +Krashen extensive input ≈ 1000ч → C2 для большинства языков.
//  Для упрощения единая шкала для всех языков (пользователь сможет редактировать цель).
export const READING_LEVELS = [
  { cef: 'A2', words: 50_000,    color: '#10b981' },
  { cef: 'B1', words: 250_000,   color: '#06b6d4' },
  { cef: 'B2', words: 1_000_000, color: '#3b82f6' },
  { cef: 'C1', words: 2_000_000, color: '#8b5cf6' },
  { cef: 'C2', words: 3_000_000, color: '#ec4899' },
];
export const LISTENING_LEVELS = [
  { cef: 'A2', hours: 50,   color: '#10b981' },
  { cef: 'B1', hours: 150,  color: '#06b6d4' },
  { cef: 'B2', hours: 400,  color: '#3b82f6' },
  { cef: 'C1', hours: 700,  color: '#8b5cf6' },
  { cef: 'C2', hours: 1000, color: '#ec4899' },
];
export const WORDS_GOAL  = READING_LEVELS[READING_LEVELS.length - 1].words;
export const LISTEN_GOAL = LISTENING_LEVELS[LISTENING_LEVELS.length - 1].hours;

export const STATS_LANG_NAMES = {
  en:'English', ru:t("Русский"), de:'Deutsch', fr:'Français', es:'Español',
  it:'Italiano', pt:'Português', zh:'中文', ja:'日本語', ko:'한국어',
  pl:'Polski', tr:'Türkçe',
};
export const STATS_LANG_FLAGS = {
  en:'🇬🇧', ru:'🇷🇺', de:'🇩🇪', fr:'🇫🇷', es:'🇪🇸',
  it:'🇮🇹', pt:'🇧🇷', zh:'🇨🇳', ja:'🇯🇵', ko:'🇰🇷', pl:'🇵🇱', tr:'🇹🇷',
};

// Окно «± минуты / слова» у кольца слушания или чтения.
//   unit: "min" или "words" — подписи и готовые значения;
//   onApply(delta): «Применить» с положительным или отрицательным числом в этих единицах.
export function StatAdjustPopover({ title, unit, onApply, onClose, theme }) {
  const C=usePalette();
  const [val, setVal] = useState('');
  const [sign, setSign] = useState(1);
  const presets = unit === 'min' ? [5, 15, 30, 60] : [50, 200, 500, 1000];
  const presetLabel = (n) => unit === 'min' ? (n >= 60 ? t("{0}ч",[n/60]) : t("{0}м",[n])) : `${n}`;
  const num = parseInt(val, 10);
  const canApply = !isNaN(num) && num > 0;
  const apply = () => { if (!canApply) return; onApply(sign * num); onClose(); };
  const seg = (on, color) => ({ flex: 1, minHeight: 40, border: 'none', borderRadius: 7, cursor: 'pointer', fontFamily: SANS, fontWeight: 600, fontSize: 14,
    background: on ? color : 'transparent', color: on ? readableOn(color) : C.mid });
  return (
    <Sheet size="sm" title={title} onClose={onClose}
      footer={<><Button onClick={onClose}>{t("Отмена")}</Button><Button variant="primary" onClick={apply} disabled={!canApply}>{t("Применить")}</Button></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div role="group" style={{ display: 'flex', gap: 4, padding: 3, background: C.surface, borderRadius: 10 }}>
          <button onClick={() => setSign(1)} aria-pressed={sign > 0} style={seg(sign > 0, C.green)}>{t("＋ Добавить")}</button>
          <button onClick={() => setSign(-1)} aria-pressed={sign < 0} style={seg(sign < 0, C.danger)}>{t("− Вычесть")}</button>
        </div>
        <Field autoFocus inputMode="numeric" value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter') apply(); }} aria-label={unit === 'min' ? t("минут") : t("слов")}
          suffix={unit === 'min' ? t("мин") : t("слов")} style={{ marginBottom: 0 }} inputStyle={{ fontSize: 17 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
          {presets.map((n) => (
            <Chip key={n} selected={val === String(n)} onClick={() => setVal(String(n))} style={{ justifyContent: 'center', minHeight: 36 }}>{presetLabel(n)}</Chip>
          ))}
        </div>
      </div>
    </Sheet>
  );
}

// Подтверждение необратимого действия (очистить статистику) — общее окно Confirm.
export function ConfirmDangerModal({ title, body, confirmLabel = t("Удалить"), onConfirm, onClose }) {
  return <Confirm title={title} body={body} confirmLabel={confirmLabel} onConfirm={onConfirm} onClose={onClose} />;
}

export function StatsScreen({ theme, navMode, activeLang, onActiveLang, onClearStats, projectLangs = [] }) {
  const C=usePalette();
  const [period, setPeriod] = useState(null);
  // Открываем на языке, который сейчас изучается (выбран вверху библиотеки), как в
  // LingQ; без него — на последнем выбранном здесь.
  const [savedLang, setLang] = useState(() => {
    try { return getActiveLang() || localStorage.getItem('ac_stats_lang') || ''; } catch { return ''; }
  });
  // ± popover + clear-confirm — local UI state, doesn't survive navigation.
  const [adjustPop, setAdjustPop] = useState(null); // {kind:'listen'|'words', rect}
  const [confirmClear, setConfirmClear] = useState(false);
  // Tick to force re-read of getTimeData/getWordsReadData after a ± adjustment.
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    const refresh = () => setReloadTick(t => t + 1);
    window.addEventListener('ac:activity-changed', refresh);
    return () => window.removeEventListener('ac:activity-changed', refresh);
  }, []);
  const store = useWordStore();

  useEffect(() => {
    try { localStorage.setItem('ac_stats_lang', savedLang); markSetting('ac_stats_lang'); } catch {}
  }, [savedLang]);
  // Язык выбран в шапке — статистика показывает его.
  useEffect(() => { if (activeLang) setLang(activeLang); }, [activeLang]);
  const pickLang = (l) => { setLang(l); if (l) onActiveLang?.(l); };

  // Languages shown as chips = union of (languages that have local stats)
  // ∪ (languages used by the user's synced projects/texts). The second half
  // matters on a fresh browser: projects sync from Supabase immediately, so
  // even before any stats data lands locally the user sees tabs for their
  // languages instead of just "Все языки". Re-runs whenever projects load.
  const availableLangs = useMemo(() => {
    const s = new Set([...getAllStatsLangs(), ...projectLangs.filter(Boolean)]);
    return Array.from(s).map(l => l.toLowerCase()).filter(Boolean).sort();
  }, [projectLangs, reloadTick]);
  // Сохранённый язык, которого больше нет среди ваших (например, «ru» из старой
  // версии), показывал нули и ни одной выбранной кнопки — тогда показываем все языки.
  const lang = savedLang && availableLangs.includes(savedLang) ? savedLang : '';
  const effLang = lang || undefined; // '' / undefined = все языки

  const stats = useMemo(() => {
    const cutoff = period ? new Date(Date.now() - period * 864e5).toISOString().slice(0, 10) : null;
    const inRange = (k) => !cutoff || k >= cutoff;

    const wordsData = getWordsReadData(effLang);
    const wordsRead = Object.entries(wordsData).reduce((s, [k, v]) => inRange(k) ? s + v : s, 0);
    const totalWordsRead = Object.values(wordsData).reduce((s, v) => s + v, 0);

    const timeData = getTimeData(effLang);
    let listenSec = 0, readSec = 0, focusedSec = 0;
    let totalListenSec = 0, totalReadSec = 0, totalFocusedSec = 0;
    Object.entries(timeData).forEach(([k, v]) => {
      totalListenSec  += (v.listen          || 0);
      totalReadSec    += (v.read            || 0);
      totalFocusedSec += (v.listen_focused  || 0);
      if (inRange(k)) {
        listenSec  += (v.listen         || 0);
        readSec    += (v.read           || 0);
        focusedSec += (v.listen_focused || 0);
      }
    });

    const actData = getActivityData(effLang);
    const cardsReviewed = Object.entries(actData).reduce((s, [k, v]) => inRange(k) ? s + v : s, 0);
    // Стрик — по дням с любыми занятиями (карточки, чтение, слушание).
    const { currentStreak, longestStreak } = calcStats(getStudyDays(effLang));

    let knownWords = 0;
    if (store) {
      const snap = store.snapshot();
      const wantLang = (lang || '').toLowerCase();
      knownWords = Object.values(snap).filter(e => {
        if (!e) return false;
        if (wantLang && (e.wordLang || '').toLowerCase() !== wantLang) return false;
        return e.status >= 4 || e.ignored;
      }).length;
    }

    return { wordsRead, totalWordsRead,
             listenSec, readSec, focusedSec,
             totalListenSec, totalReadSec, totalFocusedSec,
             cardsReviewed, currentStreak, longestStreak, knownWords };
  }, [period, store, lang, effLang, reloadTick]);

  const fmtN = (n) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M`
    : n >= 1000 ? `${(n / 1000).toFixed(1)}K`
    : String(n);
  const fmtH = (sec) => {
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
    return h > 0 ? t("{0}ч {1}м",[h,m]) : t("{0}м",[m]);
  };

  // ── Уровни: текущий + следующий ───────────────────────────────────────────
  const calcLevelInfo = (current, levels, getVal) => {
    let curLevel = null, nextLevel = levels[0];
    for (const lvl of levels) {
      if (current >= getVal(lvl)) curLevel = lvl;
      else { nextLevel = lvl; break; }
    }
    if (current >= getVal(levels[levels.length-1])) nextLevel = null;
    const goal = getVal(levels[levels.length-1]);
    const pct = Math.min(current / goal, 1);
    return { curLevel, nextLevel, pct, goal };
  };

  const totalListenHours  = stats.totalListenSec  / 3600;
  const focusedHours      = stats.totalFocusedSec / 3600;
  const bonusHours        = Math.max(0, totalListenHours - focusedHours);
  const readInfo   = calcLevelInfo(stats.totalWordsRead, READING_LEVELS,   l => l.words);
  const listenInfo = calcLevelInfo(focusedHours,         LISTENING_LEVELS, l => l.hours);

  const PERIODS = [
    { key: 1, label: t("Сегодня") }, { key: 7, label: t("7 дней") },
    { key: 30, label: t("30 дней") }, { key: null, label: t("Всё время") },
  ];

  // ── Progress Ring компонент ───────────────────────────────────────────────
  const Ring = ({ size = 180, value, total, color, big = false, labelTop, labelBottom }) => {
    const r = size / 2 - 14;
    const cx = size / 2, cy = size / 2;
    const circ = 2 * Math.PI * r;
    const pct = Math.min(value / total, 1);
    const strokeW = big ? 16 : 12;
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:'block'}}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.border} strokeWidth={strokeW}/>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={strokeW}
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}
          style={{transition:'stroke-dashoffset 0.8s ease'}}/>
        <text x={cx} y={cy - 4} textAnchor="middle" fill={C.text}
          style={{fontSize: big?28:22, fontWeight:600, fontFamily:SANS}}>{labelTop}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill={C.faint}
          style={{fontSize: big?12:11, fontFamily:SANS}}>{labelBottom}</text>
        <text x={cx} y={cy + 32} textAnchor="middle" fill={color}
          style={{fontSize: big?14:12, fontWeight:700, fontFamily:SANS}}>{(pct*100).toFixed(1)}%</text>
      </svg>
    );
  };

  // ── Milestone bar ─────────────────────────────────────────────────────────
  const LevelBar = ({ value, total, levels, getVal, formatVal }) => {
    const pct = Math.min(value / total, 1);
    return (
      <div style={{margin:'10px 0 4px'}}>
        <div style={{position:'relative', height:32}}>
          <div style={{position:'absolute',top:14,left:0,right:0,height:4,background:C.border,borderRadius:2}}/>
          <div style={{position:'absolute',top:14,left:0,width:`${pct*100}%`,height:4,
                       background:`linear-gradient(90deg, ${levels[0].color}, ${levels[levels.length-1].color})`,
                       borderRadius:2,transition:'width 0.6s ease'}}/>
          {levels.map((lvl, i) => {
            const lvlPct = getVal(lvl) / total * 100;
            const reached = value >= getVal(lvl);
            return (
              <div key={lvl.cef} style={{position:'absolute',left:`${lvlPct}%`,top:0,
                                          transform:'translateX(-50%)',display:'flex',
                                          flexDirection:'column',alignItems:'center'}}>
                <div style={{fontSize:11.5,fontWeight:700,color:reached?lvl.color:C.faint,marginBottom:2,
                             whiteSpace:'nowrap'}}>{lvl.cef}</div>
                <div style={{width:10,height:10,borderRadius:'50%',
                             background:reached?lvl.color:C.surface,
                             border:`2px solid ${reached?lvl.color:C.border}`,
                             boxShadow:reached?`0 0 0 3px ${lvl.color}22`:'none'}}/>
              </div>
            );
          })}
        </div>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:11.5,color:C.faint,marginTop:8}}>
          <span>0</span>
          <span>{formatVal(total)}</span>
        </div>
      </div>
    );
  };

  const langLabel = (l) => `${STATS_LANG_FLAGS[l] || '🌐'} ${STATS_LANG_NAMES[l] || l.toUpperCase()}`;
  const remainingHours = listenInfo.nextLevel ? Math.max(0, listenInfo.nextLevel.hours - totalListenHours) : 0;
  const remainingWords = readInfo.nextLevel   ? Math.max(0, readInfo.nextLevel.words - stats.totalWordsRead) : 0;

  return (
    <div style={{minHeight:'100vh',background:C.backdrop,fontFamily:SANS,color:C.text}}>
      <div style={{maxWidth:640,margin:'0 auto',padding:'18px 16px',boxSizing:'border-box',
                   paddingBottom:'calc(88px + var(--ac-mini-h, 0px) + var(--ac-nav-h, 0px) + env(safe-area-inset-bottom, 0px))'}}>

        {!navMode&&<h1 style={{margin:'0 0 14px',fontSize:24,fontWeight:600,color:C.text,letterSpacing:'-0.015em'}}>{t("Статистика")}</h1>}
        {/* ── Языковой селектор ─────────────────────────────────────────── */}
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:20,overflowX:'auto',paddingBottom:4}}>
          {/* общие «чипы» (ui/Chip): выбранный — включённый переключатель, тема оформляет сама */}
          <Chip icon="globe" selected={lang===''} onClick={()=>setLang('')} style={{flexShrink:0,height:36,fontSize:13.5}}>{t("Все языки")}</Chip>
          {availableLangs.map(l => (
            <Chip key={l} selected={lang===l} onClick={()=>pickLang(l)} style={{flexShrink:0,height:36,fontSize:13.5,whiteSpace:'nowrap'}}>{langLabel(l)}</Chip>
          ))}
          {availableLangs.length === 0 && (
            <div style={{fontSize:12,color:C.faint,padding:'7px 12px'}}>
              {t("Пока нет данных — начните читать или слушать")}
            </div>
          )}
        </div>

        {lang ? (
          <>
          {/* ── СЛУШАНИЕ (главный блок) — только для конкретного языка ────── */}
          <div data-panel="" style={{...panelSurface(C),borderRadius:12,
                       padding:'22px 18px',marginBottom:14,
                       position:'relative',overflow:'hidden'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
              <span style={{fontSize:24}}><Icon n="headphones"/></span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:C.mid,fontWeight:500}}>{t("Слушание")}</div>
                <div style={{fontSize:16,fontWeight:700,color:C.text}}>
                  {listenInfo.curLevel ? t("Уровень {0}",[listenInfo.curLevel.cef]) : t("Старт")}
                  {listenInfo.nextLevel && <span style={{color:C.faint,fontWeight:500,fontSize:13}}>
                    {t(" · до ")}{listenInfo.nextLevel.cef}: {Math.ceil(remainingHours)}{t("ч")}
                  </span>}
                </div>
              </div>
              <button data-btn=""
                onClick={e=>setAdjustPop({kind:'listen',rect:e.currentTarget.getBoundingClientRect()})}
                title={t("Скорректировать вручную (например, добавить часы прослушивания вне приложения)")}
                style={{background:C.surface,border:`1px solid ${C.border}`,color:C.mid,
                  width:36,height:36,borderRadius:8,cursor:'pointer',fontSize:16,
                  display:'flex',alignItems:'center',justifyContent:'center',padding:0,
                  fontFamily:SANS,fontWeight:700}}>
                ±
              </button>
            </div>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
              <Ring size={200} big value={focusedHours} total={LISTEN_GOAL}
                color={(listenInfo.nextLevel||listenInfo.curLevel||LISTENING_LEVELS[0]).color}
                labelTop={fmtH(stats.totalFocusedSec).replace('ч ',':').replace('м','')}
                labelBottom={t("/ {0}ч",[LISTEN_GOAL])}/>
            </div>
            <LevelBar value={focusedHours} total={LISTEN_GOAL} levels={LISTENING_LEVELS}
              getVal={l=>l.hours} formatVal={v=>t("{0}ч",[v])}/>
            {bonusHours>0&&(
              <div style={{marginTop:8,padding:'8px 12px',background:C.surface,borderRadius:10,
                           display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,flexWrap:'wrap',
                           fontSize:12.5,color:C.faint}}>
                <span><Icon n="target" style={{marginRight:6}}/>{t("Вдумчиво:")} <strong style={{color:C.text}}>{fmtH(stats.totalFocusedSec)}</strong></span>
                <span>{t("+ Фоном:")} <strong style={{color:C.text}}>{fmtH(bonusHours*3600)}</strong></span>
              </div>
            )}
            {/* В кольце — только вдумчивое слушание; без подсказки «0» рядом с часами фоном выглядит как ошибка. */}
            {focusedHours===0&&bonusHours>0&&(
              <div style={{marginTop:8,fontSize:12.5,color:C.mid,lineHeight:1.45}}>
                {t("Уровень растёт от вдумчивого слушания. Включите «Вдумчиво» в плеере — и время пойдёт в зачёт.")}
              </div>
            )}
          </div>

          {/* ── ЧТЕНИЕ ─────────────────────────────────────────────────────── */}
          <div data-panel="" style={{...panelSurface(C),borderRadius:12,
                       padding:'18px 18px',marginBottom:20,
                       position:'relative',overflow:'hidden'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
              <span style={{fontSize:22}}><Icon n="book"/></span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:C.mid,fontWeight:500}}>{t("Чтение")}</div>
                <div style={{fontSize:15,fontWeight:700,color:C.text}}>
                  {readInfo.curLevel ? t("Уровень {0}",[readInfo.curLevel.cef]) : t("Старт")}
                  {readInfo.nextLevel && <span style={{color:C.faint,fontWeight:500,fontSize:12}}>
                    {t(" · до ")}{readInfo.nextLevel.cef}: {fmtN(remainingWords)} {t("слов")}
                  </span>}
                </div>
              </div>
              <button data-btn=""
                onClick={e=>setAdjustPop({kind:'words',rect:e.currentTarget.getBoundingClientRect()})}
                title={t("Скорректировать вручную")}
                style={{background:C.surface,border:`1px solid ${C.border}`,color:C.mid,
                  width:36,height:36,borderRadius:8,cursor:'pointer',fontSize:16,
                  display:'flex',alignItems:'center',justifyContent:'center',padding:0,
                  fontFamily:SANS,fontWeight:700}}>
                ±
              </button>
            </div>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
              <Ring size={140} value={stats.totalWordsRead} total={WORDS_GOAL}
                color={(readInfo.nextLevel||readInfo.curLevel||READING_LEVELS[0]).color}
                labelTop={fmtN(stats.totalWordsRead)}
                labelBottom={`/ ${fmtN(WORDS_GOAL)}`}/>
            </div>
            <LevelBar value={stats.totalWordsRead} total={WORDS_GOAL} levels={READING_LEVELS}
              getVal={l=>l.words} formatVal={fmtN}/>
          </div>
          </>
        ) : (
          /* ── СУММЫ по всем языкам (без уровней — у каждого языка свой путь) ── */
          <div style={{marginBottom:20}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              <div data-panel="" style={{...panelSurface(C),borderRadius:12,
                           padding:'18px 16px',position:'relative',overflow:'hidden'}}>
                <div style={{fontSize:26,marginBottom:6}}><Icon n="headphones" s={26} sw={1.5}/></div>
                <div style={{fontSize:13,color:C.mid,fontWeight:500,marginBottom:4}}>{t("Прослушано")}</div>
                <div style={{fontSize:26,fontWeight:600,color:C.text,lineHeight:1.1}}>{fmtH(stats.totalListenSec)}</div>
                {stats.totalFocusedSec>0&&(
                  <div style={{fontSize:11,color:C.faint,marginTop:4}}>
                    <Icon n="target" s={13}/> {t("вдумчиво:")} <strong style={{color:C.text}}>{fmtH(stats.totalFocusedSec)}</strong>
                  </div>
                )}
              </div>
              <div data-panel="" style={{...panelSurface(C),borderRadius:12,
                           padding:'18px 16px',position:'relative',overflow:'hidden'}}>
                <div style={{fontSize:26,marginBottom:6}}><Icon n="book" s={26} sw={1.5}/></div>
                <div style={{fontSize:13,color:C.mid,fontWeight:500,marginBottom:4}}>{t("Прочитано слов")}</div>
                <div style={{fontSize:26,fontWeight:600,color:C.text,lineHeight:1.1}}>{fmtN(stats.totalWordsRead)}</div>
                {stats.totalReadSec>0&&(
                  <div style={{fontSize:11,color:C.faint,marginTop:4}}>
                    {t("время:")} <strong style={{color:C.text}}>{fmtH(stats.totalReadSec)}</strong>
                  </div>
                )}
              </div>
            </div>
            {availableLangs.length>0&&(
              <div data-panel="" style={{...panelSurface(C),borderRadius:12,
                           padding:'12px 14px'}}>
                <div style={{fontSize:13,fontWeight:500,color:C.mid,marginBottom:10}}>{t("По языкам")}</div>
                {availableLangs.map(l=>{
                  const td=getTimeData(l);
                  const wd=getWordsReadData(l);
                  let tFoc=0,tLis=0;
                  Object.values(td).forEach(v=>{tFoc+=(v.listen_focused||0);tLis+=(v.listen||0);});
                  const tWords=Object.values(wd).reduce((s,v)=>s+(Number(v)||0),0);
                  const lInfo=calcLevelInfo(tFoc/3600,LISTENING_LEVELS,x=>x.hours);
                  const rInfo=calcLevelInfo(tWords,READING_LEVELS,x=>x.words);
                  return(
                    <button key={l} onClick={()=>pickLang(l)}
                      style={{display:'flex',width:'100%',alignItems:'center',gap:10,
                              background:'transparent',border:'none',borderTop:`1px solid ${C.border}`,
                              padding:'10px 0',cursor:'pointer',fontFamily:SANS,color:C.text,textAlign:'left'}}>
                      <span style={{fontSize:20,flexShrink:0}}>{STATS_LANG_FLAGS[l]||'🌐'}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>
                          {STATS_LANG_NAMES[l]||l.toUpperCase()}
                        </div>
                        <div style={{fontSize:11,color:C.faint,marginTop:2}}>
                          <Icon n="headphones" s={13}/> {fmtH(tLis)} {lInfo.curLevel?`· ${lInfo.curLevel.cef}`:''}
                          {' · '}<Icon n="book" s={13}/> {fmtN(tWords)} {t("слов")} {rInfo.curLevel?`· ${rInfo.curLevel.cef}`:''}
                        </div>
                      </div>
                      <span style={{color:C.faint,fontSize:14,flexShrink:0}}>›</span>
                    </button>
                  );
                })}
                {hasUnassignedStats()&&(()=>{
                  const td=getTimeData('');const wd=getWordsReadData('');
                  const tLis=Object.values(td).reduce((s,v)=>s+(v.listen||0),0);
                  const tRead=Object.values(td).reduce((s,v)=>s+(v.read||0),0);
                  const tWords=Object.values(wd).reduce((s,v)=>s+(Number(v)||0),0);
                  return(
                    <div style={{display:'flex',alignItems:'center',gap:10,borderTop:`1px solid ${C.border}`,padding:'10px 0'}}>
                      <span style={{flexShrink:0,color:C.faint,display:'inline-flex'}}><Icon n="globe" s={20}/></span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>{t("Без языка")}</div>
                        <div style={{fontSize:11,color:C.faint,marginTop:2}}>
                          {t("Старые записи, у которых не было языка:")} <Icon n="headphones" s={13}/> {fmtH(tLis)} · <Icon n="book" s={13}/> {fmtN(tWords)} {t("слов")}{tRead?t(" · чтение {0}",[fmtH(tRead)]):''}
                        </div>
                      </div>
                      <button onClick={()=>setConfirmClear('__none__')} className="ac-ibtn"
                        style={{flexShrink:0,height:32,padding:'0 10px',background:C.lift?C.surface:"none",border:`1px solid ${C.border}`,borderRadius:8,color:C.danger,fontSize:12.5,fontWeight:600,cursor:'pointer',fontFamily:SANS}}>{t("Очистить")}</button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ── Фильтр периода (для второстепенных метрик) ─────────────────── */}
        <div style={{fontSize:13,color:C.mid,fontWeight:500,marginBottom:8}}>{t("За период")}</div>
        <div role="group" aria-label={t("Период")} style={{display:'flex',gap:4,marginBottom:14,padding:4,...panelSurface(C),borderRadius:10}}>
          {PERIODS.map(p=>(
            <button key={String(p.key)} onClick={()=>setPeriod(p.key)} aria-pressed={period===p.key}
              style={{flex:1,height:36,border:'none',borderRadius:7,cursor:'pointer',fontFamily:SANS,
                      fontSize:13,fontWeight:period===p.key?600:500,transition:'all 0.15s',
                      background:period===p.key?C.bluePale:'transparent',
                      color:period===p.key?C.blueDark:C.mid}}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Сетка метрик за период */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20}}>
          {[
            { icon: 'headphones', label: t("Прослушано"), value: fmtH(stats.listenSec), color: C.green },
            { icon: 'book', label: t("Слов прочитано"), value: fmtN(stats.wordsRead), color: C.blue },
            { icon: 'file', label: t("Чтение"), value: fmtH(stats.readSec), color: C.warm || '#e07b30' },
            { icon: 'type', label: t("Знакомых слов"), value: fmtN(stats.knownWords), color: C.blueDark },
            { icon: 'layers', label: t("Карточек"), value: fmtN(stats.cardsReviewed), color: C.blue },
            { icon: 'flame', label: t("Стрик"), value: nDays(stats.currentStreak), color: C.warm },
          ].map(item=>(
            <div key={item.label} data-panel="" style={{...panelSurface(C),
                                          borderRadius:12,padding:'14px 16px',
                                          }}>
              <div style={{marginBottom:6,color:item.color}}><Icon n={item.icon} s={22}/></div>
              <div style={{fontSize:24,fontWeight:600,color:C.text,lineHeight:1.15,marginBottom:2,fontVariantNumeric:'tabular-nums'}}>{item.value}</div>
              <div style={{fontSize:13,color:C.mid}}>{item.label}</div>
            </div>
          ))}
        </div>

        {/* Тепловая карта (фильтр по языку) */}
        <div data-panel="" style={{...panelSurface(C),borderRadius:12,
                     padding:'14px 16px',marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:500,color:C.mid,marginBottom:10}}>
            {t("Активность")}{lang?` · ${langLabel(lang)}`:''}
          </div>
          <Heatmap theme={theme} lang={effLang}/>
        </div>

        <div style={{textAlign:'center',fontSize:12,color:C.faint}}>
          {t("Лучший стрик:")} <strong style={{color:C.text}}>{nDays(stats.longestStreak)}</strong>
        </div>

        {/* ── Опасная зона: сброс статистики языка или всей ──────────────── */}
        {(
          <div style={{marginTop:32,paddingTop:18,borderTop:`1px dashed ${C.border}`}}>
            <div style={{fontSize:13,fontWeight:500,color:C.mid,marginBottom:8,textAlign:'center'}}>
              {t("Опасная зона")}
            </div>
            <button onClick={()=>setConfirmClear(lang||'__all__')}
              style={{display:'block',margin:'0 auto',background:'transparent',
                border:`1px solid ${C.border}`,color:C.danger,borderRadius:8,minHeight:40,
                padding:'8px 16px',fontSize:12,fontWeight:600,cursor:'pointer',
                fontFamily:SANS}}>
              <Icon n="trash" s={14} style={{marginRight:6}}/>{lang?t("Очистить статистику · {0}",[langLabel(lang)]):t("Очистить всю статистику (все языки)")}
            </button>
            <div style={{fontSize:11,color:C.faint,textAlign:'center',marginTop:8,lineHeight:1.5}}>
              {t("Проекты, тексты и карточки сохранятся —")}<br/>
              {t("удалятся только цифры")} {lang?t("этого языка"):t("по всем языкам")}.
            </div>
          </div>
        )}
      </div>

      {adjustPop && (
        <StatAdjustPopover
          theme={theme}
          title={adjustPop.kind==='listen'?t("Прослушано · сегодня"):t("Прочитано слов · сегодня")}
          unit={adjustPop.kind==='listen'?'min':'words'}
          anchorRect={adjustPop.rect}
          onClose={()=>setAdjustPop(null)}
          onApply={(delta)=>{
            // delta is in the popover's unit (min or words).
            const targetLang = lang || '';
            if (adjustPop.kind === 'listen') {
              // We treat manual edits as "focused" listening — that's the
              // metric that drives CEFR-level progression on the ring.
              adjustTime('listen_focused', delta * 60, targetLang);
              adjustTime('listen',         delta * 60, targetLang);
            } else {
              adjustWordsRead(delta, targetLang);
            }
          }}
        />
      )}

      {confirmClear && (() => {
        // confirmClear: код языка, '__none__' (без языка) или '__all__' (всё).
        const src = confirmClear === '__all__' ? undefined : confirmClear === '__none__' ? '' : confirmClear;
        const what = confirmClear === '__all__' ? t("всех языков") : confirmClear === '__none__' ? t("записей без языка") : langLabel(confirmClear);
        const td = getTimeData(src);
        const wd = getWordsReadData(src);
        const ad = getActivityData(src);
        const tListen = Object.values(td).reduce((s,v)=>s+(v.listen||0),0);
        const tWords  = Object.values(wd).reduce((s,v)=>s+(Number(v)||0),0);
        const tDays   = Object.keys(ad).filter(k=>ad[k]>0).length;
        const tCards  = Object.values(ad).reduce((s,v)=>s+(Number(v)||0),0);
        return (
          <ConfirmDangerModal
            theme={theme}
            title={t("Очистить статистику?")}
            body={t("Будут удалены все цифры {0}:\n",[what]) +
                  t("• {0} прослушивания\n",[fmtH(tListen)]) +
                  t("• {0} прочитанных слов\n",[fmtN(tWords)]) +
                  `• ${fmtN(tCards)} ${ruPlural(tCards,"карточка","карточки","карточек")} / ${tDays} ${ruPlural(tDays,"активный день","активных дня","активных дней")}\n\n` +
                  t("Контент (проекты, тексты, словарь) НЕ удаляется. ") +
                  t("У вас будет 6 секунд, чтобы отменить действие.")}
            confirmLabel={t("Очистить")}
            onConfirm={()=>onClearStats?.(confirmClear)}
            onClose={()=>setConfirmClear(false)}
          />
        );
      })()}
    </div>
  );
}
